// src/AllUserSearch.js
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../LoadingSpinner';// Adjust path if different
import UserCard from './AllUserCard';
export default function AllUserSearch() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // To store any fetch errors

  const { token } = useSelector(state => state.auth);

  // --- Debounce searchTerm ---
  // This useEffect updates debouncedSearchTerm only after a delay,
  // preventing too many API calls as the user types.
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce delay

    // Cleanup function: clears the timeout if searchTerm changes before the delay
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]); // Re-run effect whenever searchTerm changes

  // --- Fetch Users based on debouncedSearchTerm ---
  useEffect(() => {
    const fetchUsers = async () => {
      if (!token) {
        setLoading(false);
        setError("Authentication token not found. Please log in.");
        return;
      }

      setLoading(true); // Set loading true when starting fetch
      setError(null);   // Clear any previous errors

      // Construct the URL. If debouncedSearchTerm is empty, it will fetch all users.
      // Otherwise, it will append the username query parameter.
      const url = `https://test-api.stainedglassusa.com/users${debouncedSearchTerm ? `?username=${debouncedSearchTerm}` : ''}`;

      try {
        const response = await fetch(url, {
          headers: {
            'Authorization': `bearer ${token}`,
            'Content-Type': 'application/json', // Good practice to include
          }
        });

        if (!response.ok) {
          // Attempt to parse error message from response body
          const errorData = await response.json();
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched users data:", data);
        setUsers(data);
      } catch (e) {
        console.error("Error fetching users:", e);
        setError(`Failed to fetch users: ${e.message}`);
        setUsers([]); // Clear users on error
      } finally {
        setLoading(false); // Set loading false after fetch completes (success or error)
      }
    };

    fetchUsers();

    // No interval for fetching users. Fetch on mount and debouncedSearchTerm change only.
    // If you need real-time updates for users, consider websockets or a different strategy.
  }, [debouncedSearchTerm, token]); // Dependencies for this effect

  // --- Sorting Users (Optional Client-Side Sorting) ---
  // The backend already filters, but you might want to sort them alphabetically by username.
  const sortedUsers = useMemo(() => {
    // Sort a copy of the users array alphabetically by username
    return [...users].sort((a, b) => a.username.localeCompare(b.username));
  }, [users]); // Only re-sort if the 'users' array changes

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div>
      <h1>Search Users</h1>
      <div className="search-bar">
        <input
          id="user-search-input"
          type="text"
          placeholder="Search by Username"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>
      ) : sortedUsers.length === 0 ? (
        <p style={{ textAlign: 'center', marginTop: '20px' }}>
          No users found matching "{debouncedSearchTerm}".
          {debouncedSearchTerm === '' && " Try typing a username!"}
        </p>
      ) : (
        <div id="all-users-list" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px', padding: '20px' }}>
          {sortedUsers.map((user) => (
            <UserCard
              key={user.id} // Essential for list rendering performance
              user={user}
            />
          ))}
        </div>
      )}
    </div>
  );
}